import { JSX, ParentProps, Show, createEffect, createMemo, createSignal, createUniqueId, on, onMount } from "solid-js";
import { useAccordion } from "../AccordionContext";
import style from '../st-accordion.module.css';

export class AccordionItemProps {
    title: JSX.Element;
    disabled?: boolean;
    open?: boolean;
    titlePlacement?: 'left' | 'right';
}

export const AccordionItem = (props: AccordionItemProps & ParentProps) => {
    const accordion = useAccordion();
    const id = createUniqueId();

    const [expanded, setExpanded] = createSignal(props.open ?? false);

    createEffect(on(() => [props.open], () => {
        if (props.open) {
            accordion.open(id);
        } else {
            accordion.close(id);
        }
    }))

    createEffect(() => {
        setExpanded(accordion.expandedIds().includes(id));
    })

    const toggle = () => {
        if (props.disabled) return;
        accordion.toggle(id);
    }

    const onKeyDown = (e: KeyboardEvent) => {
        let preventDefault = true;

        const currentTarget = e.currentTarget as HTMLElement;
        switch (e.key) {
            case 'Enter':
            case ' ': 
                toggle();
                break;
            case 'ArrowLeft':
                accordion.close(currentTarget.getAttribute('data-id')!);
                break;
            case 'ArrowRight':
                accordion.open(currentTarget.getAttribute('data-id')!);
                break;
            case 'ArrowDown':
                const nextItem = currentTarget.parentElement?.nextElementSibling;
                if (nextItem) {
                    const nextHeader = nextItem.querySelector(`.${style['item-header']}`) as HTMLElement;
                    if (nextHeader) {
                        nextHeader.focus();
                    }
                }
                break;
            case 'ArrowUp':
                const prevItem = currentTarget.parentElement?.previousSibling as HTMLElement;
                if (prevItem) {
                    const prevHeader = prevItem.querySelector(`.${style['item-header']}`) as HTMLElement;
                    if (prevHeader) {
                        prevHeader.focus();
                    }
                }
                break;
            default:
                preventDefault = false
        }

        if (preventDefault) {
            e.preventDefault();
        }
    }

    return (
        <div class={style.item} data-expanded={expanded()}>
            <button class={style['item-header']} data-id={id} data-placement={props.titlePlacement ?? 'right'} onClick={toggle} onKeyDown={onKeyDown} disabled={props.disabled}>
                <i class={`${style['item-toggle-icon']} fa fa-chevron-right`} />
                {props.title}
            </button>
            <div class={style['item-content']}>
                {props.children}
            </div>
        </div>
    )
}